import { useMemo, useState, type ReactNode } from "react";
import { ExternalLink } from "lucide-react";

import type {
  TimelineActorType,
  TimelineEntry,
} from "@/application/merchants/debug_context_repository";

const ACTOR_LABELS = new Map<TimelineActorType, string>([
  ["staff", "CPOS staff"],
  ["merchant", "Merchant"],
  ["stripe", "Stripe"],
  ["system", "System"],
]);

const actorLabel = (actor: TimelineActorType) =>
  ACTOR_LABELS.get(actor) ?? actor;

const SOURCE_STYLES: Record<string, string> = {
  admin: "bg-purple-50 text-purple-700",
  lifecycle: "bg-blue-50 text-blue-700",
  stripe: "bg-indigo-50 text-indigo-700",
  billing: "bg-emerald-50 text-emerald-700",
  support: "bg-amber-50 text-amber-700",
};

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        "rounded-full border px-3 py-1 text-xs transition-colors",
        active
          ? "border-blue-600 bg-blue-50 text-blue-700"
          : "border-border text-muted-foreground hover:border-muted-foreground/40",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

/**
 * The merged merchant timeline.
 *
 * Filters are derived from the entries actually present rather than a fixed
 * list, so a source that returned nothing does not offer a chip that filters
 * to an empty view.
 */
export function MerchantTimeline({
  entries,
  showFilters = true,
}: {
  entries: TimelineEntry[];
  /**
   * Off for a short preview: filtering five already-capped entries narrows
   * a list the reader can take in whole, and the chips cost more room than
   * the entries do.
   */
  showFilters?: boolean;
}) {
  const [source, setSource] = useState<string | null>(null);
  const [actor, setActor] = useState<TimelineActorType | null>(null);

  const sources = useMemo(
    () => [...new Set(entries.map((e) => e.source))].sort(),
    [entries],
  );
  const actors = useMemo(
    () => [...new Set(entries.map((e) => e.actorType))].sort(),
    [entries],
  );

  const visible = entries.filter(
    (e) =>
      (source === null || e.source === source) &&
      (actor === null || e.actorType === actor),
  );

  return (
    <div className="space-y-4">
      {showFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <Chip active={source === null} onClick={() => setSource(null)}>
            All sources
          </Chip>
          {sources.map((s) => (
            <Chip key={s} active={source === s} onClick={() => setSource(s)}>
              {s}
            </Chip>
          ))}
          <span className="bg-muted mx-2 h-4 w-px" />
          <Chip active={actor === null} onClick={() => setActor(null)}>
            Anyone
          </Chip>
          {actors.map((a) => (
            <Chip key={a} active={actor === a} onClick={() => setActor(a)}>
              {actorLabel(a)}
            </Chip>
          ))}
        </div>
      )}

      {visible.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          Nothing matches these filters.
        </p>
      ) : (
        <ol className="divide-border border-border divide-y rounded-md border">
          {visible.map((entry) => (
            <li key={entry.id} className="flex gap-4 px-4 py-3">
              <time className="text-subtle-foreground w-44 shrink-0 font-mono text-xs">
                {new Date(entry.occurredAt).toLocaleString()}
              </time>
              <span
                className={[
                  "h-fit shrink-0 rounded px-2 py-0.5 text-xs font-medium",
                  SOURCE_STYLES[entry.source] ??
                    "bg-muted text-muted-foreground",
                ].join(" ")}
              >
                {entry.source}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-foreground text-sm">{entry.summary}</p>
                <p className="text-subtle-foreground text-xs">
                  {actorLabel(entry.actorType)}
                  {entry.actorLabel ? ` · ${entry.actorLabel}` : ""}
                </p>
              </div>
              {entry.externalUrl && (
                <a
                  href={entry.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${entry.kind} externally`}
                  className="text-subtle-foreground h-fit hover:text-blue-600"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
